// src/modules/ranking.js — vista de ranking (3 niveles × 3 categorías).
//
// FASE 4 · F1.4.b. Reescritura completa: ahora lee puntos reales desde
// Postgres vía src/core/ranking.js (que joinea puntos_prode + puntos_rituales
// con jugadores). Mantiene el empty state "El ranking arranca el 11 de junio"
// si todos los puntos están en 0 (situación normal pre-Mundial: no hay
// partidos jugados ni rituales scoreados todavía).
//
// Realtime: suscripción a puntos_prode + puntos_rituales (UPDATE). Cuando
// los triggers server-side de scoring movieron puntos (post-partido o post-
// ritual cerrado), el ranking refresca en vivo sin que el usuario recargue.
//
// Estructura visual original (HTML + estilos en línea) preservada — solo
// cambia el contenido del slot #ranking-list según la data real.

import { getDB } from '../core/db.js'
import { getCurrentUser } from '../core/auth.js'
import { navigate } from '../core/router.js'
import {
  getRankingMiGrupo,
  getRankingIntergrupal,
  getRankingNacional,
  rankingEstaVacio,
  invalidarCacheRanking,
} from '../core/ranking.js'
import { subscribirCambios, limpiarSuscripciones } from '../core/realtime.js'
import { renderActiveGroupBar, renderBottomNav } from '../ui/components.js'
import { retryCardHTML, wireRetry } from '../ui/retry-card.js'
import { appRoot } from '../utils/dom.js'
import { formatNumber } from '../utils/format.js'

const escHtml = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]
))

const EMPTY_STATE_HTML = `
  <div class="ranking-empty-state">
    <div class="ranking-empty-icon">🏆</div>
    <h3>El ranking todavía no arrancó.</h3>
    <p>Con el primer partido empieza a sumar puntos cada jugador.</p>
  </div>
`

// Cartel de bloqueo de la solapa Mi Grupo mientras el pacto NO esté certificado.
// Tono APAGADO (no alarma): todo en gris --text-muted + candado desaturado
// (grayscale + opacidad baja). Sin azul/amarillo destacado, sin colores nuevos.
const BLOQUEO_CERTIFICACION_HTML = `
  <div class="ranking-empty-state">
    <div class="ranking-empty-icon" style="filter:grayscale(1);opacity:.4;">🔒</div>
    <h3 style="color:var(--text-muted);font-weight:700;font-size:1rem;">Certificar el pacto para visualizar</h3>
    <p style="color:var(--text-muted);font-weight:400;opacity:.85;">Buscá en las redes tu cupón de certificación</p>
  </div>
`

// Ranking v1.1 §8.1: top 3 con medalla + número plano, posición 4+ solo número.
const formatPos = (posicion) => {
  if (posicion === 1) return '🥇 1'
  if (posicion === 2) return '🥈 2'
  if (posicion === 3) return '🥉 3'
  return String(posicion)
}

// Ranking v1.2 §2.4/§6.1: render del monto de última movida con signo y
// coloreo simple (verde positivo, rojo negativo, gris cero). Inline porque
// el módulo no tiene Shadow DOM ni CSS scopeado para esto. Sin sufijo "pts"
// para mantener el subtítulo lo más compacto posible.
const renderMovPuntos = (puntos) => {
  const n = Number(puntos) || 0
  const signo = n > 0 ? '+' : ''
  const color = n > 0 ? '#3ddc84' : n < 0 ? '#ef4444' : 'var(--text-muted)'
  return `<span style="color:${color};font-weight:700">${signo}${n}</span>`
}

// "Hay movida real" = existe descripción, o puntos != 0. puntos NOT NULL DEFAULT
// 0 en DB, así que el descriminador honesto es la descripción.
const hayMovida = (puntos, desc) => Boolean((desc || '').trim()) || (Number(puntos) || 0) !== 0

// Estructura: "<prefijo> <descripción> · <monto>". La descripción viene
// como dato neto desde el backend ("Cuartos · ARG vs BRA" / "Arenga"); el
// prefijo y el coloreo del monto los pone el front. Sin "· " entre prefijo
// y cuerpo para evitar duplicar separadores.
const lineaMov = (prefijo, puntos, desc) => {
  const cuerpo = []
  const d = (desc || '').trim()
  if (d) cuerpo.push(escHtml(d))
  cuerpo.push(renderMovPuntos(puntos))
  const cuerpoStr = cuerpo.join(' &middot; ')
  if (prefijo) return `<span style="opacity:.7">${prefijo}</span> ${cuerpoStr}`
  return cuerpoStr
}

// Ranking v1.2 §6.2: el subtítulo de Mi Grupo cambia según el filtro activo.
// Prefijos cortos "Últ partido:" / "Últ ritual:" identifican el origen sin
// saturar. En General se muestran las dos líneas si ambas tienen movida.
const subtituloMiGrupoUltimaMov = (fila, categoria) => {
  const hayProde = hayMovida(fila.ultimaMovidaProdePuntos, fila.ultimaMovidaProdeDescripcion)
  const hayRit = hayMovida(fila.ultimaMovidaRitualPuntos, fila.ultimaMovidaRitualDescripcion)
  const lineas = []
  if (categoria === 'prode') {
    if (hayProde) lineas.push(lineaMov('Últ partido:', fila.ultimaMovidaProdePuntos, fila.ultimaMovidaProdeDescripcion))
  } else if (categoria === 'rituales') {
    if (hayRit) lineas.push(lineaMov('Últ ritual:', fila.ultimaMovidaRitualPuntos, fila.ultimaMovidaRitualDescripcion))
  } else {
    // 'general' — ambos prefijos completos, una línea cada uno
    if (hayProde) lineas.push(lineaMov('Últ partido:', fila.ultimaMovidaProdePuntos, fila.ultimaMovidaProdeDescripcion))
    if (hayRit) lineas.push(lineaMov('Últ ritual:', fila.ultimaMovidaRitualPuntos, fila.ultimaMovidaRitualDescripcion))
  }
  if (lineas.length === 0) return '<span class="ranking-mov">Sin movidas aún</span>'
  return lineas.map((l) => `<span class="ranking-mov">${l}</span>`).join('')
}

const renderRow = (fila, posicion, categoria, level, ctx) => {
  const pos = formatPos(posicion)
  if (level === 'intergrupal') {
    // §3.4 subtítulo: "código_grupo · N jugadores · promedio".
    const score = categoria === 'prode' ? fila.promedioProde : categoria === 'rituales' ? fila.promedioRituales : fila.promedio
    const jugLabel = fila.jugadores === 1 ? 'jugador' : 'jugadores'
    const esMiGrupo = ctx.miGroupId && fila.groupId === ctx.miGroupId
    // Privacidad: el código del grupo se usa para unirse, no se muestra completo
    // en ranking público. Solo los primeros 3 chars + "***". El campo grupoCode
    // viaja completo en el dataset interno (usado por el matching del resaltado).
    const codeMasked = fila.grupoCode ? `${escHtml(fila.grupoCode.slice(0, 3))}***` : ''
    const codePart = codeMasked ? `${codeMasked} &middot; ` : ''
    return `
      <div class="ranking-item ${esMiGrupo ? 'is-me' : ''}">
        <div class="ranking-row">
          <div class="ranking-left">
            <span class="ranking-pos">${pos}</span>
            <div class="ranking-main">
              <strong>${escHtml(fila.grupoNombre || 'Grupo')}</strong>
              <div>${codePart}${fila.jugadores} ${jugLabel} &middot; promedio</div>
            </div>
          </div>
          <div class="ranking-score">
            <strong>${formatNumber(score)}</strong>
            <span>pts</span>
          </div>
        </div>
      </div>
    `
  }
  const score = categoria === 'prode' ? fila.prode : categoria === 'rituales' ? fila.rituales : fila.general
  // §4.4 Nacional: subtítulo = lista de TODOS los grupos del jugador "· "-separados.
  // §2.2/§2.4/§6.2 Mi Grupo: subtítulo = "Últ. mov" según filtro activo
  // (Prode + Ritual en General; solo el que corresponda en Prode/Rituales).
  let subtitulo
  if (level === 'nacional' && Array.isArray(fila.grupos)) {
    subtitulo = fila.grupos.map((g) => escHtml(g.nombre || '')).filter(Boolean).join(' · ')
  } else if (level === 'grupo') {
    subtitulo = subtituloMiGrupoUltimaMov(fila, categoria)
  } else {
    subtitulo = escHtml(fila.grupoNombre || '')
  }
  const esMio = level === 'nacional'
    ? (ctx.miUserId && fila.userId === ctx.miUserId)
    : (ctx.miPlayerId && fila.jugadorId === ctx.miPlayerId)
  return `
    <div class="ranking-item ${esMio ? 'is-me' : ''}">
      <div class="ranking-row">
        <div class="ranking-left">
          <span class="ranking-pos">${pos}</span>
          <div class="ranking-main">
            <strong>${escHtml(fila.apodo || 'Jugador')}</strong>
            <div>${subtitulo}</div>
          </div>
        </div>
        <div class="ranking-score">
          <strong>${formatNumber(score)}</strong>
          <span>pts</span>
        </div>
      </div>
    </div>
  `
}

const renderLista = (filas, categoria, level, ctx) => {
  if (rankingEstaVacio(filas)) return EMPTY_STATE_HTML
  return filas.map((f, i) => renderRow(f, i + 1, categoria, level, ctx)).join('')
}

const cargarFilas = async (level, codigo, categoria) => {
  const db = getDB()
  const grupo = db.grupos[codigo]
  const groupId = grupo?.group_id
  if (level === 'grupo') return getRankingMiGrupo(groupId, categoria)
  if (level === 'intergrupal') return getRankingIntergrupal(categoria)
  return getRankingNacional(categoria)
}

export const renderRanking = async (codigo) => {
  const db = getDB()
  const grupo = db.grupos[codigo]
  if (!grupo) { navigate('/ingreso'); return }

  const currentUser = getCurrentUser()
  const miPlayerId = grupo.jugadores?.find((j) => j.user_id === currentUser?.user_id)?.player_id || null
  const miUserId = currentUser?.user_id || null
  const miGroupId = grupo.group_id || null
  const ctx = { miPlayerId, miUserId, miGroupId }

  // Bloqueo de Mi Grupo hasta certificar. Mismo estado que usa el home
  // (grupo.pacto.estado === 'certificado'). Solo afecta la solapa Mi Grupo.
  const isPactoCertificado = grupo.pacto?.estado === 'certificado'

  let activeLevel = 'grupo'
  let activeCategory = 'general'

  appRoot.innerHTML = `
    <div class="view-enter" style="width:100%; margin-bottom:auto; animation:fadeIn 0.4s ease-out forwards;">
      ${renderActiveGroupBar(codigo)}
      <style>
        .ranking-level-tabs{display:grid;grid-template-columns:repeat(3,1fr);gap:.35rem;margin-bottom:1rem;background:rgba(255,255,255,.05);padding:.35rem;border-radius:12px}
        .ranking-level-btn{min-height:44px;border:0;border-radius:8px;background:transparent;color:var(--white);font-weight:800;font-size:.78rem;cursor:pointer}
        .ranking-level-btn.active{background:linear-gradient(135deg,#8bc0f0 0%,var(--celeste) 100%);color:#06111f}
        .ranking-subtabs{display:flex;gap:.5rem;margin-bottom:1.2rem;overflow-x:auto;padding-bottom:.35rem;scrollbar-width:none}
        .ranking-subtab{white-space:nowrap;border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.05);color:var(--white);border-radius:999px;padding:.42rem .9rem;font-size:.78rem;font-weight:800;cursor:pointer}
        .ranking-subtab.active{border-color:var(--celeste);color:var(--celeste);background:rgba(117,170,219,.08)}
        .ranking-list{display:flex;flex-direction:column;gap:.5rem}
        .ranking-item{border-radius:10px;overflow:hidden}.ranking-item.is-me{box-shadow:0 4px 15px rgba(117,170,219,.1)}
        .ranking-row{background:rgba(255,255,255,.03);border:1px solid transparent;border-bottom:1px solid rgba(255,255,255,.05);border-radius:8px;padding:.95rem .85rem;display:flex;align-items:center;justify-content:space-between;gap:.75rem;cursor:default;transition:background .2s}
        .ranking-item.is-me .ranking-row{background:rgba(117,170,219,.15);border-color:rgba(117,170,219,.3)}
        .ranking-left{display:flex;align-items:center;gap:.78rem;min-width:0;flex:1 1 auto}.ranking-pos{font-size:1.08rem;font-weight:900;min-width:25px;text-align:center;flex:0 0 auto}.ranking-main{min-width:0;flex:1 1 auto}.ranking-main strong{display:block;color:var(--white);font-size:.95rem;line-height:1.2;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.ranking-main div{display:grid;gap:.05rem;color:var(--text-muted);font-size:.72rem;margin-top:.2rem;line-height:1.25;min-width:0;overflow:hidden;text-overflow:ellipsis}
        .ranking-score{flex:0 0 auto}
        .ranking-mov{font-size:.62rem;line-height:1.15;opacity:.78;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
        .ranking-score{text-align:right;min-width:64px}.ranking-score strong{display:block;color:var(--white);font-size:1.08rem;line-height:1}.ranking-score span{font-size:.68rem;color:var(--text-muted)}
        .ranking-empty-state{display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:3rem 1rem;gap:.75rem}.ranking-empty-icon{font-size:4.5rem;line-height:1}
      </style>
      <div style="text-align:center;margin-bottom:1.5rem;">
        <h2 style="font-family:var(--font-heading);font-size:1.8rem;text-transform:uppercase;color:var(--white);">Ranking</h2>
      </div>
      <div class="ranking-level-tabs">
        <button class="ranking-level-btn active" data-level="grupo">Mi Grupo</button>
        <button class="ranking-level-btn" data-level="intergrupal">Intergrupal</button>
        <button class="ranking-level-btn" data-level="nacional">Nacional</button>
      </div>
      <div class="ranking-subtabs">
        <button class="ranking-subtab active" data-category="general">General</button>
        <button class="ranking-subtab" data-category="prode">Prode</button>
        <button class="ranking-subtab" data-category="rituales">Rituales</button>
      </div>
      <div class="glass-card mb-4" style="padding:1.2rem;background:rgba(0,0,0,.2);">
        <div class="ranking-list" id="ranking-list">${!isPactoCertificado ? BLOQUEO_CERTIFICACION_HTML : EMPTY_STATE_HTML}</div>
      </div>
    </div>
    ${renderBottomNav(codigo, 'ranking')}
  `

  const rerender = async () => {
    const slot = document.getElementById('ranking-list')
    if (!slot) return
    // Mi Grupo con pacto sin certificar → cartel de bloqueo en las 3 subsolapas
    // (sin fetch). Intergrupal/Nacional y Mi Grupo certificado → ranking normal.
    if (activeLevel === 'grupo' && !isPactoCertificado) { slot.innerHTML = BLOQUEO_CERTIFICACION_HTML; return }
    let filas
    try {
      filas = await cargarFilas(activeLevel, codigo, activeCategory)
    } catch (err) {
      // DT-H3-resto: la carga falló/timeouteó → "Reintentar" inline en el slot.
      console.error('[ranking] carga falló:', err?.message || err)
      slot.innerHTML = retryCardHTML('No se pudo cargar el ranking')
      wireRetry(slot, rerender)
      return
    }
    slot.innerHTML = renderLista(filas, activeCategory, activeLevel, ctx)
  }

  document.querySelectorAll('.ranking-level-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      activeLevel = btn.dataset.level
      document.querySelectorAll('.ranking-level-btn').forEach((item) => item.classList.remove('active'))
      btn.classList.add('active')
      rerender()
    })
  })

  document.querySelectorAll('.ranking-subtab').forEach((btn) => {
    btn.addEventListener('click', () => {
      activeCategory = btn.dataset.category
      document.querySelectorAll('.ranking-subtab').forEach((item) => item.classList.remove('active'))
      btn.classList.add('active')
      rerender()
    })
  })

  // Carga inicial con la data real.
  rerender()

  // F1.4.b · Realtime: cualquier UPDATE/INSERT en puntos_* refresca la vista.
  limpiarSuscripciones('ranking')
  const refrescar = () => {
    if (!window.location.hash.startsWith('#/ranking/')) return
    invalidarCacheRanking()
    rerender()
  }
  subscribirCambios('ranking', 'puntos_prode', { event: '*' }, refrescar)
  subscribirCambios('ranking', 'puntos_rituales', { event: '*' }, refrescar)
}

// ─── Bridge transitorio a window.* (sub-fase 3.8 → FASE 4) ─────────────────

if (typeof window !== 'undefined') {
  window.renderRanking = renderRanking
}
